import React, { useEffect, useRef, useState } from "react";
import { adminClient, formatAdminDate } from "@/api/adminClient";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

export default function AdminLeads() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);
  const generation = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const actionGeneration = useRef(0);
  const controlsDisabled = state !== "ready" || !!busy;
  const { query, update, reset } = useAdminListQuery();
  async function load() {
    const current = ++generation.current;
    setState("loading");
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "25" });
      for (const key of ["q", "status", "urgency", "offset"]) if (query[key]) params.set(key, query[key]);
      for (const key of ["from", "to"]) if (query[key]) params.set(key, new Date(`${query[key]}Z`).toISOString());
      const result = await adminClient(`/admin/leads?${params}`);
      if (!mounted.current || current !== generation.current) return;
      setRows(result.items || []);
      setTotal(Number(result.total) || 0);
      setState("ready");
    } catch (e) {
      if (!mounted.current || current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      generation.current += 1;
      actionGeneration.current += 1;
      busyRef.current = false;
    };
  }, [query.q, query.status, query.urgency, query.from, query.to, query.offset]);
  async function setStatus(id, status) {
    if (busyRef.current || !mounted.current || state !== "ready") return;
    const action = ++actionGeneration.current;
    busyRef.current = true;
    setBusy(id);
    setError("");
    try {
      await adminClient(`/admin/leads/${id}`, {
        method: "PATCH",
        body: { status },
      });
      if (!mounted.current || action !== actionGeneration.current) return;
      await load();
    } catch (e) {
      if (mounted.current && action === actionGeneration.current)
        setError(e.message);
    } finally {
      if (mounted.current && action === actionGeneration.current) {
        busyRef.current = false;
        setBusy(null);
      }
    }
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Leads</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Track incoming training, consulting, and access requests.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={state === "loading" || !!busy}
          className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search request, organisation, or requester" total={total} loading={state === "loading"} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter lead status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="new">New</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select></label>
        <label className="text-sm font-medium">Urgency<select aria-label="Filter lead urgency" value={query.urgency || "all"} onChange={(event) => update({ urgency: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All urgency</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
        <label className="text-sm font-medium">From (UTC)<input aria-label="Filter leads from date" type="datetime-local" value={query.from || ""} onChange={(event) => update({ from: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
        <label className="text-sm font-medium">To (UTC)<input aria-label="Filter leads to date" type="datetime-local" value={query.to || ""} onChange={(event) => update({ to: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
      </AdminListToolbar>
      {error && (
        <p
          className="rounded-lg border border-destructive/40 bg-card p-4 text-sm text-destructive"
          role="alert"
        >
          {error}{" "}
          <button type="button" onClick={load} className="ml-2 underline">
            Retry
          </button>
        </p>
      )}
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading leads…
        </p>
      )}
      {state === "ready" && rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No requests yet.
        </p>
      ) : (
        rows.length > 0 &&
        state !== "idle" && (
          <div
            role="region"
            aria-label="Lead requests table"
            tabIndex={0}
            className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <table className="w-full min-w-[700px] text-left text-sm">
              <caption className="sr-only">
                Training and consulting requests
              </caption>
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Request</th>
                  <th className="px-4 py-3">Organisation</th>
                  <th className="px-4 py-3">Requester</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-0"
                  >
                    <th scope="row" className="px-4 py-3 font-semibold">
                      {row.title}
                      <span className="mt-1 block text-xs font-normal">Reference: {row.reference || row.id}</span>
                      <span className="mt-1 block text-xs font-normal text-muted-foreground">
                        {row.type} · {row.urgency}
                      </span>
                    </th>
                    <td className="px-4 py-3">
                      {row.organisation || "Access request (no organisation)"}
                    </td>
                    <td className="px-4 py-3">
                      {row.requested_by_name}
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {row.requested_by_email}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatAdminDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <label>
                        <span className="sr-only">Status for {row.title}</span>
                        <select
                          value={row.status}
                          disabled={controlsDisabled}
                          onChange={(e) => setStatus(row.id, e.target.value)}
                          className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50"
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={controlsDisabled} onPage={(offset) => update({ offset }, { resetOffset: false })} />
    </div>
  );
}
